import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { litDate } from '../../lib/periodes';
import { suiviAffaires } from '../../lib/suivi-affaires';
import { compteDansLeCa, ttcDocument } from '../../lib/vat';
import { C } from '../../theme/palette';

/** Une case : combien de pièces, et combien d'argent. */
function Case({ icone, libelle, suffixe, n, montant, couleur, note, onClick }) {
  return (
    <div
      role="button"
      data-testid="case-suivi"
      onClick={onClick}
      style={{
        background: C.white,
        border: '1px solid ' + C.border,
        borderLeft: '4px solid ' + couleur,
        borderRadius: 11,
        padding: '10px 11px',
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 9.5, fontWeight: 800, color: C.grey }}>
        {icone} {tr(libelle)}
        {suffixe ? ' ' + suffixe : ''}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        {n != null && (
          <span style={{ fontSize: 20, fontWeight: 900, color: couleur, lineHeight: 1 }}>{n}</span>
        )}
        <span style={{ fontSize: 11, fontWeight: 800, color: C.ink, whiteSpace: 'nowrap' }}>
          {DEVISE} {fmt(montant)}
        </span>
      </div>
      {note && (
        <div style={{ fontSize: 8.5, color: C.amber, marginTop: 4, lineHeight: 1.35 }}>{note}</div>
      )}
    </div>
  );
}

/**
 * « Mes offres et factures » : pour chaque étape, combien de pièces et pour
 * combien d'argent — offres en attente, acceptées, refusées, ce qui reste à
 * facturer, factures pas envoyées, envoyées, payées, en retard. Chaque case
 * ouvre le module concerné ; le détail d'un chantier est sur sa page.
 *
 * <p>Le patron trouvait le suivi chantier par chantier trop chargé pour
 * l'accueil : il voulait les chiffres, simplement.
 *
 * @param {object} props propriétés
 * @param {object[]} props.devis offres
 * @param {object[]} props.factures factures clients
 * @param {object[]} [props.chantiers] chantiers
 * @param {(module: string) => void} props.onNav navigation
 * @param {boolean} [props.estMag] au magasin, il n'y a pas de chantier : ce qui
 *   reste à facturer s'ouvre dans les offres
 * @param {object[]} [props.pieces] factures découpées comme la comptabilité
 *   (un acompte reçu l'année d'avant à part)
 * @param {Date} [props.maintenant] date de référence
 * @returns {import('react').ReactElement} la carte
 */
export function SuiviAffaires({
  devis,
  factures,
  chantiers,
  onNav,
  estMag = false,
  pieces,
  maintenant = new Date(),
}) {
  const s = suiviAffaires({ devis, factures, chantiers, estMag });
  const aFacturer = s.affaires.filter((a) => a.reste > 0.005).length;
  // « Factures payées » : l'argent reçu cette année. Toutes les années
  // mêlées, un acompte reçu et déclaré en 2025 passait pour gagné en 2026.
  const annee = maintenant.getFullYear();
  const payeesAn = (pieces || factures || []).filter(
    (f) =>
      f.type !== 'avoir' &&
      f.statut === 'Payée' &&
      compteDansLeCa(f) &&
      litDate(f.paidDate || f.date, maintenant)?.getFullYear() === annee,
  );
  const payees = {
    n: new Set(payeesAn.map((f) => f.partDe ?? f.id)).size,
    ttc: payeesAn.reduce((t, f) => t + ttcDocument(f), 0),
  };
  // Les acomptes de ces mêmes factures touchés l'année d'avant : la case le
  // dit, sans les recompter (« + 10'877.00 touchés en 2025 = 88'092.31 »).
  const ids = new Set(payeesAn.map((f) => String(f.partDe ?? f.id)));
  const avant = (pieces || []).filter(
    (f) =>
      f.partDe != null &&
      ids.has(String(f.partDe)) &&
      (litDate(f.paidDate || f.date, maintenant)?.getFullYear() ?? annee) < annee,
  );
  const avantTtc = avant.reduce((t, f) => t + ttcDocument(f), 0);
  const notePayees =
    avantTtc > 0
      ? trf(
          '+ {0} d’acomptes touchés en {1} = {2} en tout',
          fmt(avantTtc),
          annee - 1,
          fmt(payees.ttc + avantTtc),
        )
      : null;
  const cases = [
    ['⏳', 'Offres en attente', s.enAttente.n, s.enAttente.ttc, C.amber, 'devis'],
    ['✅', 'Offres acceptées', s.acceptees.n, s.acceptees.ttc, C.green, 'devis'],
    ['❌', 'Offres refusées', s.refusees.n, s.refusees.ttc, C.red, 'devis'],
    ['⏩', 'Reste à facturer', null, s.resteAFacturer, C.navy, estMag ? 'devis' : 'chantiers'],
    [
      '✏️',
      'Factures pas envoyées',
      s.brouillons.n,
      s.brouillons.ttc,
      C.purple,
      'factures',
      'Brouillon',
    ],
    [
      '📨',
      'Factures envoyées, pas payées',
      s.nonPayees.n,
      s.nonPayees.ttc,
      C.amber,
      'factures',
      'Envoyée',
    ],
    [
      '💰',
      'Factures payées',
      payees.n,
      payees.ttc,
      C.green,
      'factures',
      'Payée',
      annee,
      notePayees,
    ],
    ['⏰', 'Factures en retard', s.enRetard.n, s.enRetard.ttc, C.red, 'factures', 'Envoyée'],
  ];
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ padding: '12px 12px 4px' }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: C.ink }}>
          {tr('📋 Mes offres et factures')}
        </div>
        <div style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>
          {tr('En ce moment · montants TTC · touche une case pour voir le détail')}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 8,
          padding: '8px 12px 12px',
        }}
      >
        {cases.map(([icone, libelle, n, montant, couleur, vers, filtre, suffixe, note]) => (
          <Case
            key={libelle}
            icone={icone}
            libelle={libelle}
            suffixe={suffixe}
            n={n}
            montant={montant}
            note={note}
            couleur={n === 0 ? C.muted : couleur}
            onClick={() => (filtre ? onNav(vers, { filtre }) : onNav(vers))}
          />
        ))}
      </div>
      {/* Seulement les chantiers qui ont encore quelque chose à facturer :
          un chantier entièrement facturé se comptait « en cours de
          facturation » à côté de « Reste à facturer 0.00 ». */}
      {!estMag && aFacturer > 0 && (
        <div style={{ fontSize: 9, color: C.muted, padding: '0 12px 12px' }}>
          {trf(
            'Le détail de chaque chantier (acompte, facture finale, paiements) est dans sa page : Chantiers → {0} chantier(s) en cours de facturation.',
            aFacturer,
          )}
        </div>
      )}
    </Card>
  );
}
